import { useRouterTransitioning } from "@faire/deprecated-retailer-visitor-shared/app/_lib/routing/useRouterTransitioning";
import {
  SHOW_MODAL_QUERY_PARAM,
  CONDUCTOR_MODAL_QUERY_PARAM,
} from "@faire/deprecated-retailer-visitor-shared/components/App/AppModals/consts";
import { useConductorComponent } from "@faire/deprecated-retailer-visitor-shared/features/Conductor/useConductorComponent";
import { useSettingFoiForFdSequentialModals } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingFoiForFdSequentialModals";
import { useMilestone } from "@faire/marketplace-lib/stores/useMilestone";
import { CONDUCTOR_MODAL_CONTENT_IDENTIFIER_QUERY_PARAM_KEY } from "@faire/sdui/lib/consts";
import { trackFaireDirectPopFoiModalOnPageNavActionCategoryOther } from "@faire/web-api--source/events/faireDirect/actionCategoryOther/popFoiModalOnPageNav";
import { ConductorSurface } from "@faire/web-api--source/indigofair/data/ConductorSurface";
import { FeatureName } from "@faire/web-api--source/indigofair/data/FeatureName";
import { FIRST_ORDER_INCENTIVE_MODAL_DISMISS } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/FIRST_ORDER_INCENTIVE_MODAL_DISMISS";
import { getWindow } from "@faire/web/common/globals/getWindow";
import { Storage } from "@faire/web/common/Storage";
import { addDays } from "date-fns/addDays";
import { isBefore } from "date-fns/isBefore";
import { History } from "history";
import qs from "qs";
import { useEffect, useRef } from "react";

export const FOI_MODAL_SHOWN_STORAGE_KEY = "FOI_MODAL_SHOW_AGAIN_AT";

export const useFaireDirectPopFoiModalOnPageNavEffect = (history: History) => {
  const initialPathRef = useRef<string | null>(null);
  const { hasOccurred: hasDismissedFoiModal } = useMilestone(
    FIRST_ORDER_INCENTIVE_MODAL_DISMISS
  );

  const { isTransitioning } = useRouterTransitioning();
  const isTransitionStarted = useRef(false);
  const shouldShowModal = useRef(false);
  const isFoiForFdSequentialModals = useSettingFoiForFdSequentialModals();
  const { componentToken, featureName } = useConductorComponent({
    surface: ConductorSurface.SITE_WIDE_MODAL,
  });

  useEffect(() => {
    if (!isFoiForFdSequentialModals) {
      return;
    }
    // first time loading page
    if (initialPathRef.current === null) {
      initialPathRef.current = history.location.pathname;
      return;
    }
    if (
      hasDismissedFoiModal ||
      getFoiModalShownLessThan24hrsAgo() ||
      history.location.pathname === initialPathRef.current ||
      !componentToken
    ) {
      return;
    }

    shouldShowModal.current = true;

    const newFoiModalShowAgainAt = addDays(new Date(), 1).getTime().toString();
    Storage.local.setItem(
      FOI_MODAL_SHOWN_STORAGE_KEY,
      newFoiModalShowAgainAt.toString()
    );
  }, [
    history.location.pathname,
    hasDismissedFoiModal,
    initialPathRef,
    isFoiForFdSequentialModals,
    componentToken,
  ]);

  useEffect(() => {
    if (!isFoiForFdSequentialModals) {
      return;
    }

    if (isTransitioning) {
      isTransitionStarted.current = true;
      return;
    }

    // track if transition has completed (from true to false)
    const isTransitionComplete =
      isTransitionStarted.current && !isTransitioning;

    if (
      shouldShowModal.current &&
      isTransitionComplete &&
      featureName === FeatureName.FIRST_ORDER_INCENTIVE
    ) {
      trackFaireDirectPopFoiModalOnPageNavActionCategoryOther();

      const queryParams = qs.stringify({
        ...qs.parse(history.location.search),
        [SHOW_MODAL_QUERY_PARAM]: CONDUCTOR_MODAL_QUERY_PARAM,
        [CONDUCTOR_MODAL_CONTENT_IDENTIFIER_QUERY_PARAM_KEY]: componentToken,
      });
      const urlWithModalQueryParam = `${history.location.pathname}?${queryParams}`;

      getWindow()?.history.replaceState(null, "", urlWithModalQueryParam);
      shouldShowModal.current = false;
      isTransitionStarted.current = false;
    }
  }, [history, isTransitioning, isFoiForFdSequentialModals, componentToken]);

  const getFoiModalShownLessThan24hrsAgo = () => {
    const foiModalShowAgainAt = Storage.local.getItem(
      FOI_MODAL_SHOWN_STORAGE_KEY
    );
    const parsedTimestamp = foiModalShowAgainAt
      ? Number(foiModalShowAgainAt)
      : null;
    const foiModalShowAgainTimestamp =
      parsedTimestamp !== null && !Number.isNaN(parsedTimestamp)
        ? parsedTimestamp
        : null;
    return (
      foiModalShowAgainTimestamp !== null &&
      isBefore(new Date(), new Date(foiModalShowAgainTimestamp))
    );
  };
};
